import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { t } from '@aquabox/shared';
import { api } from '../api/client';
import type { Order, Page } from '../api/types';
import {
  loadSoundPreference,
  playAlert,
  saveSoundPreference,
  unlockAudio,
} from '../lib/alert-sound';

/**
 * Siparişler listesi için kaynak listesi (görev §1.2).
 *
 * Kanal siparişleri (HAYAT_PANEL, ABANT_PANEL, GETIR_SU) ses çalar; telefon/WhatsApp
 * (MANUAL) çalmaz — patron kendi girdiği sipariş için uyarı beklemez.
 */
const CHANNEL_SOURCES: ReadonlyArray<Order['source']> = ['HAYAT_PANEL', 'ABANT_PANEL', 'GETIR_SU'];

/**
 * Sesli bildirim gerektiren sipariş mi. HANDLED_EXTERNALLY (Hayat'ta zaten kapanmış; geçmiş
 * aktarımı ya da ilk görüşte kapalı gelen) yapılacak iş taşımaz — sunucu da ORDER_RECEIVED
 * yazmaz. Bugünün geçmişi aktarılınca her satır için ses çalmasın.
 */
const needsAlert = (o: Order): boolean =>
  CHANNEL_SOURCES.includes(o.source) && o.status !== 'HANDLED_EXTERNALLY';

/** "Yeni" sayılma penceresi (ms): mount anından bu kadar eski kanal siparişleri hâlâ yenidir.
 *  İlk tur hata alırsa sonraki başarılı turda bu pencere sayesinde yeni sipariş yutulmaz. */
const RECENT_WINDOW_MS = 60_000;

/** Ses tercihi butonu panelin ana başlığında (`Layout.tsx`) yaşar; burası yalnız izleyici. */
export interface NewOrderWatcherProps {
  /** Sekme başlığına yazılacak taban ad. Belirtilmezse "AquaBox — Panel". */
  baseTitle?: string;
  /** Polling aralığı (ms). Üretimde 15_000; testler kısa verebilir. */
  pollIntervalMs?: number;
}

/**
 * Kanal siparişlerini izler, gelince ses + toast + sekme başlığı sayacı günceller.
 *
 * - 15 sn'de bir `/api/orders?dateFilter=today&pageSize=50&sort=receivedAt&dir=desc` çağrılır.
 * - İlk BAŞARILI yüklemede görünen siparişler "görülmüş" sayılır; bu arada `receivedAt`'ı
 *   mount anının 60 saniye penceresinde olan kanal siparişleri "yeni" sayılır ve bildirilir.
 *   İlk tur hata alırsa `seenIdsRef` null kalır; sonraki başarılı tur aynı mantıkla çalışır
 *   ve mount sonrası gelen yeni sipariş YUTULMAZ.
 * - Sonraki turlarda yeni kimlik gelirse ve `source` kanal kaynaklarından biriyse → ses + toast.
 * - Aynı kimlik tekrar gelirse ses 2. kez ÇALMAZ (görülmüş kümesi bellekte).
 * - Sekme başlığı okunmamış kanal siparişi sayısını taşır; `/siparisler` ekranına girilince sayaç sıfırlanır.
 * - Yeni kanal siparişinde `['orders']` sorgusu invalidate edilir — açık ekrandaysa 15 sn
 *   beklenmeden liste hemen yenilensin.
 * - İlk kullanıcı etkileşiminden sonra AudioContext unlock olur; ses düğmesi AÇIK'a çevrilince
 *   bir örnek ses çal ki kullanıcı gerçekten çaldığını duysun.
 *
 * Test sahteleme: ses çalmayı `vi.mock('../lib/alert-sound')` ile sahtele. Toast kendi içinde
 * render edilir (useToast'a onClick geçirilemiyor — `Toast.tsx` dışımızda). Render edilen
 * toast bir düğme, tıklanınca `/siparisler`'e gider; mevcut yönlendirme deseniyle uyumlu.
 *
 * Bildirim kasıtlı olarak otomatik kapanMAZ: acil sipariş gözden kaçmasın diye kullanıcı
 * tıklayana ya da × düğmesine basana kadar kalır (erişilebilirlik: `role="status"` +
 * `aria-live="polite"`).
 */
export function NewOrderWatcher({
  baseTitle = 'AquaBox — Panel',
  pollIntervalMs = 15_000,
}: NewOrderWatcherProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();

  // Ses tercihi. localStorage'dan bir kez okunur ve değiştiğinde yeniden yazılır.
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => loadSoundPreference());
  useEffect(() => {
    saveSoundPreference(soundEnabled);
  }, [soundEnabled]);

  // Ses tercihini polling effect bağımlılığından çıkarmak için ref tutuyoruz — düğme her
  // değiştiğinde polling döngüsünü sıfırlamak fazladan istek atar.
  const soundEnabledRef = useRef<boolean>(soundEnabled);
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Bileşen kurulduğu an. İlk başarılı turdaki "yeni mi eski mi?" kararı bu ana göre:
  // mount anının 60 sn penceresinde gelen kanal siparişleri "yeni" sayılır. İlk tur hata
  // alırsa `seenIdsRef` null kalır; sonraki başarılı tur aynı pencereyle değerlendirir
  // ve mount sonrasında gelen siparişi yutmaz.
  const mountedAtRef = useRef<number>(Date.now());

  // Görülmüş sipariş kimlikleri. İlk başarılı turda doldurulur, sonraki turlarda yeni kimlikler
  // için ses + toast tetikler. Sayfa yenilenince sıfırlanır — ilk yükleme kuralı yeniden geçerli.
  const seenIdsRef = useRef<Set<string> | null>(null);

  // Son görülen kanal siparişleri (toast metni için). Görülmüş kümesi belliyken bile
  // metni göstermek için state'de tutulur.
  const [latestNew, setLatestNew] = useState<{
    id: string;
    source: Order['source'];
    customerName: string;
  } | null>(null);

  // Okunmamış kanal siparişi sayacı. /siparisler ekranına girilince sıfırlanır.
  const [unreadCount, setUnreadCount] = useState(0);

  // Sekme başlığı.
  useEffect(() => {
    document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
  }, [unreadCount, baseTitle]);

  // /siparisler ekranına girilince sayaç sıfırlanır.
  const onOrdersRef = useRef(false);
  useEffect(() => {
    const nowOnOrders = pathname.startsWith('/siparisler');
    if (nowOnOrders && !onOrdersRef.current) {
      setUnreadCount(0);
    }
    onOrdersRef.current = nowOnOrders;
  }, [pathname]);

  // İlk kullanıcı etkileşiminden sonra AudioContext'i kur/uyar. Dinleyici kendini kaldırır;
  // unmount'ta da güvenle kaldırılır (zaten varsa no-op).
  useEffect(() => {
    const handler = (): void => {
      void unlockAudio();
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', handler);
    };
    document.addEventListener('pointerdown', handler);
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', handler);
    };
  }, []);

  // Polling: 15 sn'de bir sipariş listesi.
  // `soundEnabled` bağımlılık listesinde DEĞİL — ref üzerinden okunuyor; düğme basışında
  // effect'in yeniden kurulup fazladan istek atmasını engeller.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const page = await api.get<Page<Order>>(
          '/api/orders?dateFilter=today&pageSize=50&sort=receivedAt&dir=desc',
        );
        if (cancelled) return;
        const seen = seenIdsRef.current;
        if (seen === null) {
          // İlk BAŞARILI yükleme: kimlikleri görülmüş say, AMA `receivedAt`'ı son
          // 60 saniyede olan kanal siparişleri hâlâ "yeni"dir. İlk tur hata alırsa
          // buraya hiç düşülmez; sonraki başarılı turda pencere hâlâ açık olduğu için
          // mount sonrasında gelen siparişler doğru biçimde bildirilir.
          const next = new Set<string>();
          const cutoff = mountedAtRef.current - RECENT_WINDOW_MS;
          for (const o of page.items) {
            next.add(o.id);
            const orderTime = Date.parse(o.receivedAt);
            if (needsAlert(o) && Number.isFinite(orderTime) && orderTime > cutoff) {
              setLatestNew({ id: o.id, source: o.source, customerName: o.customerName });
              setUnreadCount((c) => c + 1);
              if (soundEnabledRef.current) playAlert();
              // Sipariş tablosu açıksa 15 sn beklenmesin — liste şimdi yenilensin.
              void qc.invalidateQueries({ queryKey: ['orders'] });
            }
          }
          seenIdsRef.current = next;
          return;
        }
        for (const o of page.items) {
          if (seen.has(o.id)) continue;
          seen.add(o.id);
          if (needsAlert(o)) {
            // Yeni kanal siparişi.
            setLatestNew({ id: o.id, source: o.source, customerName: o.customerName });
            setUnreadCount((c) => c + 1);
            if (soundEnabledRef.current) playAlert();
            void qc.invalidateQueries({ queryKey: ['orders'] });
          }
        }
      } catch {
        // Sessizce yut: polling hatası panelin çalışmasını engellemesin.
      } finally {
        if (!cancelled) timer = setTimeout(tick, pollIntervalMs);
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [qc, pollIntervalMs]);

  // Ses düğmesi AÇIK'a çevrilince AudioContext'i uyar + bir örnek ses çal ki kullanıcı
  // gerçekten çaldığını duysun. KAPALI'ya çevrilince ek çağrı yok.
  const handleToggleSound = (): void => {
    setSoundEnabled((prev) => {
      const next = !prev;
      if (next) {
        void unlockAudio();
        playAlert();
      }
      return next;
    });
  };

  return (
    <>
      {latestNew && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            right: 28,
            bottom: 28,
            zIndex: 50,
            maxWidth: 420,
          }}
        >
          <button
            type="button"
            data-testid="new-order-toast"
            onClick={() => {
              setLatestNew(null);
              void navigate('/siparisler');
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              background: 'var(--surface)',
              borderLeft: '4px solid var(--success)',
              borderRadius: 'var(--r-menu)',
              padding: '14px 36px 14px 18px',
              boxShadow: '0 6px 24px rgba(16,30,56,.12)',
              fontWeight: 500,
              fontSize: 14,
              color: 'var(--text-primary)',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {t('panel.newOrderToast', {
              channel: t(`orderSource.${latestNew.source}` as Parameters<typeof t>[0]),
              customer: latestNew.customerName,
            })}
          </button>
          {/* Acil siparişi gözden kaçırmamak için toast otomatik kapanMAZ: kullanıcı
              tıklayana ya da bu × düğmesine basana kadar KALIR. */}
          <button
            type="button"
            data-testid="new-order-toast-close"
            aria-label={t('panel.newOrderToastClose')}
            onClick={() => setLatestNew(null)}
            style={{
              position: 'absolute',
              top: 6,
              right: 10,
              background: 'transparent',
              border: 0,
              fontSize: 18,
              lineHeight: 1,
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            ×
          </button>
        </div>
      )}
      <button
        type="button"
        data-testid="sound-toggle"
        aria-pressed={soundEnabled}
        onClick={handleToggleSound}
        style={{
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-row)',
          padding: '6px 10px',
          fontSize: 12.5,
          color: soundEnabled ? 'var(--success)' : 'var(--text-secondary)',
          cursor: 'pointer',
        }}
      >
        {soundEnabled ? `🔔 ${t('panel.soundOn')}` : `🔕 ${t('panel.soundOff')}`}
      </button>
    </>
  );
}
